import { useState, useEffect, useContext } from 'react'
import { useMutation, useLazyQuery } from "@apollo/client";
import { useNavigate } from "react-router";
import { useDisclosure, useToast } from "@chakra-ui/react";
import { v4 as uuidv4 } from 'uuid';
import { uploadWithSasToken, uploadDALLEWithSasToken, extractTextFromFile, callGenerateCourseOutline, callGenerateDyno } from "../utils/helper";
import {
    CREATE_PRODUCT,
    CREATE_AI_CONTENT,
} from "../graphql/mutations";
import {
    DYNOCATEGORIES
} from "../graphql/queries";
import webSocketStore from "../Store/webSocketStore";
import { textTrackServiceWebSocketURL } from '../config'
import { PublisherAndUserContext } from '../contexts/PublisherAndUserContext';
import { useBreadcrumbs } from "../contexts/BreadcrumbProvider"
import {useStore} from "../Store"

export const useCreateProductPage = ({uuid, product, currentStep, updateStates, type}) => {
    const { user, publisher } = useContext(PublisherAndUserContext);
    const { addMultiBreadCrumb } = useBreadcrumbs();
    const {resetStatesBook, resetStatesCourse, bookOutline, courseOutline} = useStore((state) => state);

    const [dynoCategories, setDynoCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState("");
    const [prompt, setPrompt] = useState("");
    const [isManual, setIsManual] = useState(true)
    const [editMode, setEditMode] = useState(false)
    const [isUploadingFromCreateProduct, setIsUploadingFromCreateProduct] = useState(false)
    const {
        isOpen,
        onOpen,
        onClose,
    } = useDisclosure();
    const connectWebSocket = webSocketStore((state) => state.connectWebSocket);

    const navigate = useNavigate();
    const toast = useToast();

    const fileTypePath = {
        "pdf": "pdf/parse",
        "video": "video/extract-text",
        "image": "image/extract-text",
        "audio": "audio/extract-text"
    }

    const getFileData = (file) => {
        const data = {
            "pdf": {
                pdfUrl: file.url,
                uuid: file.uuid,
                containerPath: `${publisher.id}/coursecontent`,
                publisherId: publisher?.id
            },
            "video": {
                videoId: file.id,
                uuid: file.id,
                publisherId: publisher?.id
            },
            "image": {
                imageUrl: file.url,
                uuid: file.uuid,
                publisherId: publisher?.id
            },
            "audio": {
                audioUrl: file.url,
                uuid: file.uuid,
                publisherId: publisher?.id
            }
        }
        return JSON.stringify(data[file.fileType ? file.fileType : file.type.split('s')[0]])
    }

    const handleNext = () => {
        if (currentStep === 1 && !product?.category?.id) {
            toast({
                position: "top-right",
                title: "Please select the audience",
                status: "warning",
                duration: 3000,
                isClosable: true,
            });
        } else if (currentStep === 2 && product.category.name === "PK-12" && (product.grades.length === 0 || !product.subject)) {
            toast({
                position: "top-right",
                title: `Please select ${product.grades.length === 0 && !product.subject ? "grade and subject" : !product.subject ? "subject" : "grade"}`,
                status: "warning",
                duration: 3000,
                isClosable: true,
            });
        } else if (currentStep === 2 && (product.category.name === "HigherEd" && !product.subCategory)) {
            toast({
                position: "top-right",
                title: "Please select subcategory",
                status: "warning",
                duration: 3000,
                isClosable: true,
            });
        } else {
            navigate(`/create${type}/${currentStep + 1}`)
            updateStates({[`${type}CurrentStep`]: currentStep + 1})
        }
    };

    const onProductDetailSubmit = async (e) => {
        e.preventDefault()
        let sasUrl = null
        if (product.coverPath instanceof File) {
            sasUrl = await uploadWithSasToken(publisher?.id, "images", product.coverPath);
            const productContent = {
                ...product,
                coverPath: sasUrl.split("?")[0],
            }
            updateStates({
                [type]: productContent
            })
        }
        if (!sasUrl && product.coverPath instanceof File) return alert("Error uploading image");
        navigate(`/create${type}/${currentStep + 1}`)
        updateStates({[`${type}CurrentStep`]: currentStep + 1})
    }

    const handleBack = () => {
        navigate(`/create${type}/${currentStep - 1}`)
        updateStates({[`${type}CurrentStep`]: currentStep - 1})
    };

    const [createProduct] = useMutation(CREATE_PRODUCT, {
        onCompleted: async (data) => {
            setLoading(false)
            setLoadingMessage("")
            onClose()
            if (isManual) {
                if(type === "book") {
                    resetStatesBook()
                } else {
                    resetStatesCourse()
                }
                addMultiBreadCrumb([{ label: "Home", path: "/" }, { label: "Catalog", path: "/catalogs" }, { label: data.edshopifyCreateStripeProduct.catalog.catalogName, path: `/catalog/${data.edshopifyCreateStripeProduct.id}` }, { label: data.edshopifyCreateStripeProduct.productName, path: "" }])
                navigate(`/content/${data.edshopifyCreateStripeProduct.id}`);
            } else {
                updateStates({[`${type}ProgressLoading`]: true})
                try {
                    updateStates({
                        [type]: {
                            ...product,
                            id: data.edshopifyCreateStripeProduct.id,
                            uuid: data.edshopifyCreateStripeProduct.id,
                            catalogName: data.edshopifyCreateStripeProduct.catalog.catalogName
                        }
                    })
                    connectWebSocket(`wss://${textTrackServiceWebSocketURL}`, [data.edshopifyCreateStripeProduct.id])
                    callGenerateDyno({outline: type === "book" ? bookOutline.modules : courseOutline.modules, productId: data.edshopifyCreateStripeProduct.id, publisherId: publisher?.id })
                } catch(e) {
                    toast({
                        position: "top-right",
                        title: e,
                        status: "error",
                        duration: 4000,
                        isClosable: true,
                    });
                }
            }
        },
        onError: () => {
            setLoading(false)
            toast({
                position: "top-right",
                title: "Failed to create course. Please try again.",
                status: "error",
                duration: 4000,
                isClosable: true,
            });
        },
    });

    const parsePseudoJSON = (pseudoJSON) => {
        // '{\n    name: "Helen Keller",\n    heading: "Helen Keller: An Inspirational Life",\n    description: "Helen Keller was an amazing woman who overcame incredible challenges. She was born on June 27, 1880, in Alabama, USA. When she was just 19 months old, she got very sick and lost her sight and hearing. This made it very hard for her to communicate. But with the help of her teacher, Anne Sullivan, Helen learned how to read, write, and even speak. She went on to graduate from college and became a famous author and activist, helping others with disabilities. Her story shows that with determination and the right support, we can overcome difficulties and achieve great things.",\n    grade: "5",\n    subject: "English"\n}'
        // Replace single-line keys with quoted keys
        const correctedJSON = pseudoJSON.replace(/(\w+):/g, '"$1":');
        return JSON.parse(correctedJSON);
    };

    const [createAIContent] = useMutation(
        CREATE_AI_CONTENT,
        {
            onCompleted: async (data) => {
                const content = data.edshopifyCreateAIProducts.content
                const sasUrl = await uploadDALLEWithSasToken(publisher?.id, "images", data.edshopifyCreateAIProducts.imagesRes, content.name, uuidv4())
                if (!sasUrl) return alert("Error uploading image");
                if (content.name !== "" && content.heading !== "" && content.description !== "") {
                    const productContent = {
                        ...product,
                        name: content.name,
                        type: type === "book" ? "Book" : "Course",
                        heading: content.heading,
                        description: content.description,
                        coverPath: sasUrl.split("?")[0],
                    }
                    updateStates({
                        [type]: productContent
                    })
                    setPrompt("")
                    setLoading(false);
                } else {
                    setLoading(false);
                    toast({
                        position: "top-right",
                        title: "AI server is busy. Please try again.",
                        status: "error",
                        duration: 4000,
                        isClosable: true,
                    });
                }
            },
            onError: (err) => {
                setLoading(false)
                toast({
                    position: "top-right",
                    title: "There was an error while generating AI content. Please try again.",
                    status: "error",
                    duration: 4000,
                    isClosable: true,
                });
            },
        }
    );

    const [getDynosCategories] = useLazyQuery(DYNOCATEGORIES,
        {
            onCompleted: (data) => {
                setDynoCategories([data.dynoCategories.find(dc => dc.name === "PK-12"), ...data.dynoCategories.filter(dc => dc.name !== "PK-12")])
            }
        }
    )

    const createProductInDB = async (e) => {
        e.preventDefault();
        setLoading(true);
        setLoadingMessage(`creating ${type}...`)
        let gradesId = product.grades.map((grade) => ({ id: grade }));
        let chapters = null
        const outline = type === "book" ? bookOutline : courseOutline
        if(outline?.modules?.length > 0) {
            chapters = {
                create: {
                    dynoList: {
                        create: outline.modules.map(mod => ({ listName: mod.moduleTitle, listTitle: mod.moduleTitle })),
                    },
                },
            }
        }
        updateStates({[type]: {
            ...product,
            uuid: uuidv4()
        }})
        createProduct({
            variables: {
                data: {
                    productName: product.name,
                    productType: type === "book" ? "Book" : "Course",
                    productHeading: product.heading,
                    productDescription: product.description,
                    isAddedToStore: false,
                    productPricingType: null,
                    productAdmins: {
                        connect: [{ id: user.id }],
                    },
                    ...(gradesId.length > 0 ? {
                        productGrades: {
                            connect: gradesId,
                        }
                    } : {}),
                    ...(product.subject ? {
                        productSubjects: {
                            connect: {
                                id: product.subject
                            },
                        }
                    } : {}),
                    productCategory: {
                        connect: {
                            id: product?.subCategory?.id ? product?.subCategory?.id : product?.category?.id,
                        },
                    },
                    ...(type === "book" ? {
                        bookPublisher: {
                            connect: {
                                id: publisher.bookPublishers[0].id
                            }
                        }
                    } : {
                        courseInstructor: {
                            connect: {
                                id: publisher.instructors[0].id
                            }
                        }
                    }),
                    productCoverPath: product.coverPath,
                    ...(chapters ? {productContent: { ...chapters }} : {}),
                    catalog: {
                        connect: {
                            id: publisher?.catalogs[0].id
                        }
                    }
                },
                where: {
                    id: publisher?.id,
                },
            },
        });
    };

    const generateContent = async (e) => {
        setIsManual(false)
        await createProductInDB(e, false)
    }

    const createContent = (e) => {
        e.preventDefault();
        setLoading(true);
        setLoadingMessage("Generating Title, Heading, Description, Image...")
        createAIContent({
            variables: {
                where: {
                    id: publisher?.id
                },
                data: {
                    userMessage: prompt
                }
            }
        })
    }

    const extractTextFromAllFiles = async (uploaded, allFiles) => {
        setIsUploadingFromCreateProduct(true)
        setLoadingMessage("Completed: Reading The Content")
        if(uploaded?.length > 0) {
            updateStates({workingUUID: uploaded.map(u => u.uuid)})
            await Promise.all(uploaded.map((file) => {
                const path = fileTypePath[file.type.split('s')[0]]
                const data = getFileData(file)
                connectWebSocket(`wss://${textTrackServiceWebSocketURL}`, [file.uuid])
                return extractTextFromFile(path, data)
            }))
        }
        const payload = {
            courseTitle: product.name,
            courseDescription: product.description,
            selectedUuids: allFiles.filter(fileToExtractText => fileToExtractText.includeInOutline).map(f => f.uuid ? f.uuid : f.id),
            publisherId: publisher?.id,
            origin: "edshop"
        }
        updateStates({workingUUID: [payload.selectedUuids[0]]})
        setLoadingMessage("Completed: Generating Outline")
        connectWebSocket(`wss://${textTrackServiceWebSocketURL}`, payload.selectedUuids)
        const mods = await callGenerateCourseOutline(payload)
        updateStates({[`${type}Outline`]: mods})
        setIsUploadingFromCreateProduct(false)
        updateStates({workingUUID: []})
        setLoadingMessage("")
        if(mods?.length === 0) {
            toast({
                position: "top-right",
                title: "Please try again.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
            return null
        }
        onOpen()
    }

    const onModuleTitleChange = (e, index) => {
        const outline = type === "book" ? bookOutline : courseOutline;
        const updatedModule = { 
            ...outline.modules[index], 
            moduleTitle: e.target.value
        };

        outline.modules[index] = updatedModule;

        updateStates({ [`${type}Outline`]: outline });
    };

    const onOutlineChange = (e, dynoType, dynoIndex, index) => {
        let outline = type === "book" ? bookOutline : courseOutline;
        const updatedModule = { ...outline.modules[index] };

        const sectionItems = [...(updatedModule.content[dynoType] || [])];
        sectionItems[dynoIndex] = {
          ...sectionItems[dynoIndex],
          title: e.target.value
        };
        updatedModule.content[dynoType] = sectionItems;

        outline.modules[index] = updatedModule;
    
        updateStates({ [`${type}Outline`]: outline });
    };

    useEffect(() => {
        getDynosCategories({
            variables: {
                where: {
                    parentDynoCategoryId: {
                        equals: null
                    }
                }
            }
        })
    }, []);

    return {
        dynoCategories,
        loading,
        handleBack,
        handleNext,
        product,
        createContent,
        setPrompt,
        onProductDetailSubmit,
        createProductInDB,
        isOpen,
        onOpen,
        onClose,
        generateContent,
        extractTextFromAllFiles,
        prompt,
        isUploadingFromCreateProduct,
        loadingMessage,
        onOutlineChange,
        onModuleTitleChange,
        editMode, 
        setEditMode
    }
}